/**
 * SupportCaseDetailPanel
 * ---------------------------------------------------------------------------
 * One support case, opened from the queue. Rendered as a wide modal over
 * SupportCaseQueue so the queue keeps its filters, its page and its live socket
 * subscription while a case is being handled.
 *
 * What it shows:
 *   - the header facts, with the SLA read and the breach marker
 *   - the full message thread, public replies and internal notes distinguished
 *   - a composer that posts either, with the internal flag set explicitly
 *   - the evidence list, plus an add-evidence row
 *   - the status history
 *   - assignment, escalation, status change, reopen
 *   - resolution capture: outcome, summary, and the financial impact
 *
 * Two rules this file lives by:
 *
 * 1. The SERVER is authoritative. Nothing here decides a permission, a
 *    transition, an SLA or a priority. The status select offers every status the
 *    live vocabulary names and lets the server refuse an illegal transition with
 *    SUPPORT_CASE_INVALID_TRANSITION, which is surfaced as its message. The same
 *    goes for assignment and resolution: a 403 becomes a sentence, not a guess.
 *
 * 2. A field may simply be ABSENT. Results are scoped per caller, so a vendor
 *    manager can legitimately receive a case with no `sla`, no `tags` and no
 *    `resolution.financialImpact`. Every read here is optional and every block
 *    is conditional, so a scoped payload renders a smaller panel rather than a
 *    blank screen.
 *
 * "case", never "ticket".
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import moment from "moment";
import {
  apiAddSupportCaseEvidence,
  apiAssignSupportCase,
  apiEscalateSupportCase,
  apiGetSupportCase,
  apiPostSupportCaseMessage,
  apiReopenSupportCase,
  apiResolveSupportCase,
  apiUpdateSupportCaseStatus,
  supportCaseErrorMessage,
} from "../api/supportCases";
import {
  SHIPPED_CASE_VOCABULARY,
  CASE_EVIDENCE_TYPES,
  caseAssigneeTypeLabel,
  caseAuthorRoleLabel,
  caseCategoryLabel,
  casePriorityLabel,
  casePriorityTone,
  caseResolutionOutcomeLabel,
  caseStatusLabel,
  caseStatusTone,
  caseTeamLabel,
  describeCaseSla,
  isFinancialResolutionOutcome,
  isTerminalCaseStatus,
  normalizeCasePayload,
  partitionCaseMessages,
  toCaseOptions,
} from "../utilities/supportCaseVocabulary";
import AppSelect from "../components/ui/AppSelect";

const CaseChip = ({ tone, children }) => (
  <span className={`support-case-chip tone-${tone}`}>{children}</span>
);

const Fact = ({ label, children }) => {
  if (children == null || children === "" || children === false) return null;
  return (
    <div>
      <div className="support-case-fact-label">{label}</div>
      <div className="support-case-fact-value">{children}</div>
    </div>
  );
};

const when = (value, fallback = "") =>
  value ? moment(value).format("MMM D, YYYY h:mm A") : fallback;

const money = (impact) => {
  if (!impact) return "";
  const amount = Number(impact.amount);
  if (!Number.isFinite(amount)) return "";
  const currency = impact.currency ? `${impact.currency} ` : "";
  return `${currency}${amount.toLocaleString()}`;
};

const authorLabel = (message) =>
  message?.authorName ||
  caseAuthorRoleLabel(message?.authorRole) ||
  "Someone";

const SupportCaseDetailPanel = ({
  caseId,
  show,
  onHide,
  onCaseChanged,
  refreshSignal = 0,
  vocabulary = SHIPPED_CASE_VOCABULARY,
}) => {
  // State first, then every memo that reads it. Declaring a memo above the state
  // it consumes is the no-use-before-define the lint gate fails on.
  const [supportCase, setSupportCase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");

  const [reply, setReply] = useState("");
  const [replyIsInternal, setReplyIsInternal] = useState(false);

  const [statusDraft, setStatusDraft] = useState("");
  const [statusNote, setStatusNote] = useState("");
  // The server files a cancellation in the compliance trail and refuses one with
  // no reason, so the form asks for it rather than letting the call come back 400.
  const cancelNoteRequired = statusDraft === "CANCELLED";

  const [assignDraft, setAssignDraft] = useState({ assigneeType: "", assignee: "", team: "" });

  const [escalationReason, setEscalationReason] = useState("");

  const [resolutionDraft, setResolutionDraft] = useState({
    outcome: "",
    summary: "",
    currency: "",
    amount: "",
    refundId: "",
  });

  const [evidenceDraft, setEvidenceDraft] = useState({ url: "", type: "image", caption: "" });

  const threadRef = useRef(null);

  const statusOptions = useMemo(
    () => toCaseOptions(vocabulary.statuses, caseStatusLabel),
    [vocabulary.statuses]
  );
  const teamOptions = useMemo(() => toCaseOptions(vocabulary.teams, caseTeamLabel), [vocabulary.teams]);
  const assigneeTypeOptions = useMemo(
    () => toCaseOptions(vocabulary.assigneeTypes, caseAssigneeTypeLabel),
    [vocabulary.assigneeTypes]
  );
  const outcomeOptions = useMemo(
    () => toCaseOptions(vocabulary.resolutionOutcomes, caseResolutionOutcomeLabel),
    [vocabulary.resolutionOutcomes]
  );

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError("");
    try {
      const response = await apiGetSupportCase(caseId);
      const detail = normalizeCasePayload(response?.data);
      if (!detail) {
        setError("That case could not be read.");
        return;
      }
      setSupportCase(detail);
      setStatusDraft(detail.status || "");
      setAssignDraft({
        assigneeType: detail.assignment?.assigneeType || "",
        assignee: "",
        team: detail.assignment?.team || "",
      });
      setResolutionDraft((prev) => ({
        ...prev,
        outcome: detail.resolution?.outcome || prev.outcome,
        summary: detail.resolution?.summary || prev.summary,
        currency:
          detail.resolution?.financialImpact?.currency || detail.currency || prev.currency,
      }));
    } catch (loadError) {
      setError(supportCaseErrorMessage(loadError, "Failed to load the case."));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (!show || !caseId) return;
    load();
  }, [show, caseId, refreshSignal, load]);

  // A fresh open starts with a clean composer rather than the previous case's
  // half-typed reply.
  useEffect(() => {
    setReply("");
    setReplyIsInternal(false);
    setStatusNote("");
    setEscalationReason("");
    setNotice("");
    setEvidenceDraft({ url: "", type: "image", caption: "" });
  }, [caseId]);

  const thread = useMemo(() => partitionCaseMessages(supportCase?.messages), [supportCase]);

  useEffect(() => {
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [thread.thread.length]);

  const sla = useMemo(() => describeCaseSla(supportCase), [supportCase]);

  const evidence = useMemo(
    () => (Array.isArray(supportCase?.evidence) ? supportCase.evidence : []),
    [supportCase]
  );

  const history = useMemo(
    () => (Array.isArray(supportCase?.statusHistory) ? supportCase.statusHistory : []),
    [supportCase]
  );

  const settled = isTerminalCaseStatus(supportCase?.status);

  /**
   * Every mutating action funnels through here: one busy flag, one error
   * surface, one refresh, and one upward notification so the queue row and the
   * summary strip follow without a full reload.
   */
  const run = useCallback(
    async (key, action, successMessage) => {
      setBusy(key);
      setError("");
      setNotice("");
      try {
        const response = await action();
        const updated = normalizeCasePayload(response?.data);
        if (updated) {
          setSupportCase(updated);
          setStatusDraft(updated.status || "");
        } else {
          await load();
        }
        if (successMessage) setNotice(successMessage);
        if (onCaseChanged) onCaseChanged(updated || null);
        return true;
      } catch (actionError) {
        setError(supportCaseErrorMessage(actionError, "That action did not go through."));
        return false;
      } finally {
        setBusy("");
      }
    },
    [load, onCaseChanged]
  );

  const sendMessage = async () => {
    const body = reply.trim();
    if (!body) return;
    const internal = replyIsInternal;
    const ok = await run(
      "message",
      () => apiPostSupportCaseMessage(caseId, { body, isInternal: internal }),
      internal ? "Internal note added." : "Reply sent."
    );
    if (ok) setReply("");
  };

  const changeStatus = async () => {
    if (!statusDraft || statusDraft === supportCase?.status) return;
    const note = statusNote.trim();
    const ok = await run(
      "status",
      () => apiUpdateSupportCaseStatus(caseId, { status: statusDraft, ...(note ? { note } : {}) }),
      `Status set to ${caseStatusLabel(statusDraft)}.`
    );
    if (ok) setStatusNote("");
  };

  const saveAssignment = async () => {
    const body = {};
    if (assignDraft.assigneeType) body.assigneeType = assignDraft.assigneeType;
    if (assignDraft.assignee.trim()) body.assignee = assignDraft.assignee.trim();
    if (assignDraft.team) body.team = assignDraft.team;
    if (!Object.keys(body).length) return;
    const ok = await run("assign", () => apiAssignSupportCase(caseId, body), "Assignment saved.");
    if (ok) setAssignDraft((prev) => ({ ...prev, assignee: "" }));
  };

  const escalate = async () => {
    const reason = escalationReason.trim();
    const ok = await run(
      "escalate",
      () => apiEscalateSupportCase(caseId, reason ? { reason } : {}),
      "Case escalated."
    );
    if (ok) setEscalationReason("");
  };

  const resolve = async () => {
    if (!resolutionDraft.outcome) {
      setError("Choose an outcome before resolving the case.");
      return;
    }
    const body = { outcome: resolutionDraft.outcome };
    const summary = resolutionDraft.summary.trim();
    if (summary) body.summary = summary;
    const amount = Number(resolutionDraft.amount);
    if (Number.isFinite(amount) && String(resolutionDraft.amount).trim() !== "") {
      body.financialImpact = {
        amount,
        ...(resolutionDraft.currency ? { currency: resolutionDraft.currency } : {}),
        ...(resolutionDraft.refundId.trim() ? { refundId: resolutionDraft.refundId.trim() } : {}),
      };
    }
    await run("resolve", () => apiResolveSupportCase(caseId, body), "Case resolved.");
  };

  const reopen = () =>
    run("reopen", () => apiReopenSupportCase(caseId, {}), "Case reopened.");

  const addEvidence = async () => {
    const url = evidenceDraft.url.trim();
    if (!url) return;
    const entry = {
      url,
      type: evidenceDraft.type || "image",
      ...(evidenceDraft.caption.trim() ? { caption: evidenceDraft.caption.trim() } : {}),
    };
    const ok = await run(
      "evidence",
      () => apiAddSupportCaseEvidence(caseId, { evidence: [entry] }),
      "Evidence added."
    );
    if (ok) setEvidenceDraft({ url: "", type: "image", caption: "" });
  };

  const anyBusy = Boolean(busy);

  return (
    <Modal show={show} onHide={onHide} size="xl" className="support-case-detail" scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="div" style={{ width: "100%" }}>
          <div className="support-case-detail-head">
            <div className="support-case-number">{supportCase?.caseNumber || "Support case"}</div>
            <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>
              {supportCase?.subject || (loading ? "Loading" : "Case")}
            </div>
            <div className="support-case-detail-chips">
              {supportCase?.status ? (
                <CaseChip tone={caseStatusTone(supportCase.status)}>
                  {caseStatusLabel(supportCase.status)}
                </CaseChip>
              ) : null}
              {supportCase?.priority ? (
                <CaseChip tone={casePriorityTone(supportCase.priority)}>
                  {casePriorityLabel(supportCase.priority)}
                </CaseChip>
              ) : null}
              {supportCase?.category ? (
                <CaseChip tone="muted">{caseCategoryLabel(supportCase.category)}</CaseChip>
              ) : null}
              {sla.breached ? <CaseChip tone="urgent">{sla.breachLabel}</CaseChip> : null}
              {supportCase?.isIncident ? <CaseChip tone="urgent">Incident</CaseChip> : null}
              {Number(supportCase?.escalation?.level) > 0 ? (
                <CaseChip tone="warn">Escalation level {supportCase.escalation.level}</CaseChip>
              ) : null}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error ? (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        ) : null}
        {notice ? (
          <Alert variant="success" onClose={() => setNotice("")} dismissible>
            {notice}
          </Alert>
        ) : null}

        {loading && !supportCase ? (
          <div className="support-case-empty">
            <Spinner animation="border" size="sm" /> Loading the case
          </div>
        ) : null}

        {supportCase ? (
          <>
            {/* --- Facts ------------------------------------------------- */}
            <div className="support-case-section">
              <div className="support-case-section-title">Case</div>
              <div className="support-case-facts">
                <Fact label="Raised by">
                  {supportCase.raisedBy?.name ||
                    caseAuthorRoleLabel(supportCase.raisedBy?.role) ||
                    null}
                </Fact>
                <Fact label="Raised">{when(supportCase.createdAt) || null}</Fact>
                <Fact label="Last activity">{when(supportCase.lastActivityAt) || null}</Fact>
                <Fact label="Order">{supportCase.order?.number || null}</Fact>
                <Fact label="Vendor">{supportCase.vendor?.name || null}</Fact>
                <Fact label="Rider">{supportCase.rider?.name || null}</Fact>
                <Fact label="Assigned to">
                  {supportCase.assignment?.assigneeName ||
                    (supportCase.assignment?.assigneeType
                      ? caseAssigneeTypeLabel(supportCase.assignment.assigneeType)
                      : null)}
                </Fact>
                <Fact label="Team">{caseTeamLabel(supportCase.assignment?.team) || null}</Fact>
                <Fact label="SLA">{sla.label}</Fact>
                <Fact label="Resolution due">{when(sla.dueAt) || null}</Fact>
                <Fact label="Reopened">
                  {Number(supportCase.reopenCount) > 0 ? `${supportCase.reopenCount} times` : null}
                </Fact>
                <Fact label="Satisfaction">
                  {supportCase.satisfaction?.rating
                    ? `${supportCase.satisfaction.rating} out of 5`
                    : null}
                </Fact>
              </div>
              {sla.breached ? (
                <div className="support-case-breach-flag" style={{ marginTop: 8 }}>
                  SLA breached: {sla.breachLabel}
                </div>
              ) : null}
              {Array.isArray(supportCase.tags) && supportCase.tags.length ? (
                <div style={{ marginTop: 8 }}>
                  {supportCase.tags.map((tag) => (
                    <Badge bg="light" text="dark" key={tag} className="me-1">
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {supportCase.description ? (
                <p className="support-case-message-body" style={{ marginTop: 10 }}>
                  {supportCase.description}
                </p>
              ) : null}
            </div>

            {/* --- Thread ------------------------------------------------ */}
            <div className="support-case-section">
              <div className="support-case-section-title">
                Conversation: {thread.publicReplies.length} public, {thread.internalNotes.length}{" "}
                internal
              </div>
              {thread.thread.length ? (
                <div className="support-case-thread" ref={threadRef}>
                  {thread.thread.map((message, index) => (
                    <div
                      key={message._id || `${message.createdAt || ""}-${index}`}
                      className={`support-case-message${message.isInternal ? " is-internal" : ""}`}
                    >
                      <div className="support-case-message-head">
                        <span className="support-case-message-author">
                          {authorLabel(message)}
                          {message.authorRole ? (
                            <span className="support-case-message-when">
                              {" "}
                              {caseAuthorRoleLabel(message.authorRole)}
                            </span>
                          ) : null}
                          {message.isInternal ? (
                            <span className="support-case-internal-tag" style={{ marginLeft: 6 }}>
                              Internal note
                            </span>
                          ) : null}
                        </span>
                        <span className="support-case-message-when">{when(message.createdAt)}</span>
                      </div>
                      <p className="support-case-message-body">{message.body}</p>
                      {Array.isArray(message.attachments) && message.attachments.length ? (
                        <div>
                          {message.attachments.map((attachment, attachmentIndex) => (
                            <a
                              className="support-case-attachment"
                              href={attachment.url}
                              key={attachment.url || attachmentIndex}
                              rel="noreferrer noopener"
                              target="_blank"
                            >
                              {attachment.name || attachment.type || "Attachment"}
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="support-case-meta">No messages on this case yet.</div>
              )}

              {/* Composer. The internal flag is an explicit, visible choice:
                  nothing is inferred from who is signed in, and the server
                  refuses an internal note from a caller not allowed to leave
                  one. */}
              <div className={`support-case-composer${replyIsInternal ? " is-internal" : ""}`}>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={
                    replyIsInternal
                      ? "Internal note. The customer will not see this."
                      : "Reply. The customer will see this."
                  }
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  disabled={anyBusy}
                />
                <div className="support-case-composer-actions">
                  <Form.Check
                    type="switch"
                    id="support-case-internal-toggle"
                    label="Internal note, not visible to the customer"
                    checked={replyIsInternal}
                    onChange={(event) => setReplyIsInternal(event.target.checked)}
                    disabled={anyBusy}
                  />
                  <Button
                    variant={replyIsInternal ? "warning" : "primary"}
                    size="sm"
                    onClick={sendMessage}
                    disabled={anyBusy || !reply.trim()}
                  >
                    {busy === "message" ? (
                      <Spinner animation="border" size="sm" />
                    ) : replyIsInternal ? (
                      "Add internal note"
                    ) : (
                      "Send reply"
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* --- Evidence -------------------------------------------- */}
            <div className="support-case-section">
              <div className="support-case-section-title">Evidence: {evidence.length}</div>
              {evidence.length ? (
                <div className="support-case-evidence">
                  {evidence.map((item, index) => (
                    <a
                      href={item.url}
                      key={item.url || index}
                      rel="noreferrer noopener"
                      target="_blank"
                      title={item.caption || item.url}
                    >
                      {item.caption || item.type || `Evidence ${index + 1}`}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="support-case-meta">Nothing attached to this case.</div>
              )}
              <Row className="g-2" style={{ marginTop: 8 }}>
                <Col md={5}>
                  <Form.Control
                    size="sm"
                    placeholder="Evidence link"
                    value={evidenceDraft.url}
                    onChange={(event) =>
                      setEvidenceDraft((prev) => ({ ...prev, url: event.target.value }))
                    }
                    disabled={anyBusy}
                  />
                </Col>
                <Col md={2}>
                  <AppSelect block
                    size="sm"
                    value={evidenceDraft.type}
                    onChange={(event) =>
                      setEvidenceDraft((prev) => ({ ...prev, type: event.target.value }))
                    }
                    disabled={anyBusy}
                  >
                    {CASE_EVIDENCE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </AppSelect>
                </Col>
                <Col md={3}>
                  <Form.Control
                    size="sm"
                    placeholder="Caption"
                    value={evidenceDraft.caption}
                    onChange={(event) =>
                      setEvidenceDraft((prev) => ({ ...prev, caption: event.target.value }))
                    }
                    disabled={anyBusy}
                  />
                </Col>
                <Col md={2}>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={addEvidence}
                    disabled={anyBusy || !evidenceDraft.url.trim()}
                    className="w-100"
                  >
                    {busy === "evidence" ? <Spinner animation="border" size="sm" /> : "Attach"}
                  </Button>
                </Col>
              </Row>
            </div>

            {/* --- Handling -------------------------------------------- */}
            <div className="support-case-section">
              <div className="support-case-section-title">Handling</div>
              <Row className="g-2">
                <Col md={3}>
                  <Form.Label className="support-case-fact-label">Status</Form.Label>
                  <AppSelect block
                    size="sm"
                    value={statusDraft}
                    onChange={(event) => setStatusDraft(event.target.value)}
                    disabled={anyBusy}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppSelect>
                </Col>
                <Col md={6}>
                  <Form.Label className="support-case-fact-label">
                    {cancelNoteRequired ? "Why is this being stopped" : "Note on the change"}
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder={cancelNoteRequired ? "Required" : "Optional"}
                    value={statusNote}
                    onChange={(event) => setStatusNote(event.target.value)}
                    disabled={anyBusy}
                    isInvalid={cancelNoteRequired && statusNote.trim().length < 3}
                  />
                  {cancelNoteRequired ? (
                    <Form.Text muted>
                      Cancelling a case is recorded in the compliance trail, so it needs a reason.
                    </Form.Text>
                  ) : null}
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="w-100"
                    onClick={changeStatus}
                    disabled={
                      anyBusy ||
                      !statusDraft ||
                      statusDraft === supportCase.status ||
                      (cancelNoteRequired && statusNote.trim().length < 3)
                    }
                  >
                    {busy === "status" ? <Spinner animation="border" size="sm" /> : "Update status"}
                  </Button>
                </Col>
              </Row>

              <Row className="g-2" style={{ marginTop: 10 }}>
                <Col md={3}>
                  <Form.Label className="support-case-fact-label">Assign to</Form.Label>
                  <AppSelect block
                    size="sm"
                    value={assignDraft.assigneeType}
                    onChange={(event) =>
                      setAssignDraft((prev) => ({ ...prev, assigneeType: event.target.value }))
                    }
                    disabled={anyBusy}
                  >
                    <option value="">Leave as is</option>
                    {assigneeTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppSelect>
                </Col>
                <Col md={3}>
                  <Form.Label className="support-case-fact-label">Team</Form.Label>
                  <AppSelect block
                    size="sm"
                    value={assignDraft.team}
                    onChange={(event) =>
                      setAssignDraft((prev) => ({ ...prev, team: event.target.value }))
                    }
                    disabled={anyBusy}
                  >
                    <option value="">Leave as is</option>
                    {teamOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppSelect>
                </Col>
                <Col md={3}>
                  <Form.Label className="support-case-fact-label">Assignee user id</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Optional"
                    value={assignDraft.assignee}
                    onChange={(event) =>
                      setAssignDraft((prev) => ({ ...prev, assignee: event.target.value }))
                    }
                    disabled={anyBusy}
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="w-100"
                    onClick={saveAssignment}
                    disabled={anyBusy}
                  >
                    {busy === "assign" ? <Spinner animation="border" size="sm" /> : "Save assignment"}
                  </Button>
                </Col>
              </Row>

              <Row className="g-2" style={{ marginTop: 10 }}>
                <Col md={9}>
                  <Form.Label className="support-case-fact-label">Escalation reason</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Why this needs to go up a level"
                    value={escalationReason}
                    onChange={(event) => setEscalationReason(event.target.value)}
                    disabled={anyBusy}
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    size="sm"
                    variant="outline-warning"
                    className="w-100"
                    onClick={escalate}
                    disabled={anyBusy}
                  >
                    {busy === "escalate" ? <Spinner animation="border" size="sm" /> : "Escalate"}
                  </Button>
                </Col>
              </Row>
              {supportCase.escalation?.reason ? (
                <div className="support-case-meta" style={{ marginTop: 6 }}>
                  Last escalation: {supportCase.escalation.reason}
                  {supportCase.escalation.escalatedAt
                    ? `, ${when(supportCase.escalation.escalatedAt)}`
                    : ""}
                </div>
              ) : null}
            </div>

            {/* --- Resolution ------------------------------------------ */}
            <div className="support-case-section">
              <div className="support-case-section-title">Resolution</div>
              {supportCase.resolution?.outcome ? (
                <div className="support-case-facts" style={{ marginBottom: 10 }}>
                  <Fact label="Outcome">
                    {caseResolutionOutcomeLabel(supportCase.resolution.outcome)}
                  </Fact>
                  <Fact label="Resolved">{when(supportCase.resolution.resolvedAt) || null}</Fact>
                  <Fact label="Resolved by">
                    {supportCase.resolution.resolvedByRole
                      ? caseAuthorRoleLabel(supportCase.resolution.resolvedByRole)
                      : null}
                  </Fact>
                  {/* financialImpact is redacted for some callers, so this block
                      only appears when the field actually arrived. */}
                  <Fact label="Financial impact">
                    {money(supportCase.resolution.financialImpact) || null}
                  </Fact>
                  <Fact label="Refund reference">
                    {supportCase.resolution.financialImpact?.refundId || null}
                  </Fact>
                </div>
              ) : null}
              {supportCase.resolution?.summary ? (
                <p className="support-case-message-body">{supportCase.resolution.summary}</p>
              ) : null}

              <Row className="g-2">
                <Col md={4}>
                  <Form.Label className="support-case-fact-label">Outcome</Form.Label>
                  <AppSelect block
                    size="sm"
                    value={resolutionDraft.outcome}
                    onChange={(event) =>
                      setResolutionDraft((prev) => ({ ...prev, outcome: event.target.value }))
                    }
                    disabled={anyBusy}
                  >
                    <option value="">Choose an outcome</option>
                    {outcomeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppSelect>
                </Col>
                <Col md={8}>
                  <Form.Label className="support-case-fact-label">What was done</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    placeholder="Summary the next person reading this case will rely on"
                    value={resolutionDraft.summary}
                    onChange={(event) =>
                      setResolutionDraft((prev) => ({ ...prev, summary: event.target.value }))
                    }
                    disabled={anyBusy}
                  />
                </Col>
              </Row>

              <Row className="g-2" style={{ marginTop: 10 }}>
                <Col md={2}>
                  <Form.Label className="support-case-fact-label">Currency</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="UGX"
                    value={resolutionDraft.currency}
                    onChange={(event) =>
                      setResolutionDraft((prev) => ({ ...prev, currency: event.target.value }))
                    }
                    disabled={anyBusy}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="support-case-fact-label">
                    Amount
                    {isFinancialResolutionOutcome(resolutionDraft.outcome) ? "" : " (optional)"}
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={resolutionDraft.amount}
                    onChange={(event) =>
                      setResolutionDraft((prev) => ({ ...prev, amount: event.target.value }))
                    }
                    disabled={anyBusy}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="support-case-fact-label">Refund reference</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Optional"
                    value={resolutionDraft.refundId}
                    onChange={(event) =>
                      setResolutionDraft((prev) => ({ ...prev, refundId: event.target.value }))
                    }
                    disabled={anyBusy}
                  />
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    size="sm"
                    variant="success"
                    className="w-100"
                    onClick={resolve}
                    disabled={anyBusy || !resolutionDraft.outcome}
                  >
                    {busy === "resolve" ? <Spinner animation="border" size="sm" /> : "Resolve case"}
                  </Button>
                </Col>
              </Row>
            </div>

            {/* --- History -------------------------------------------- */}
            <div className="support-case-section">
              <div className="support-case-section-title">Status history</div>
              {history.length ? (
                <ul className="support-case-history">
                  {history.map((entry, index) => (
                    <li key={`${entry.at || ""}-${index}`}>
                      {entry.from ? `${caseStatusLabel(entry.from)} to ` : "Set to "}
                      <strong>{caseStatusLabel(entry.to)}</strong>
                      {entry.byRole ? `, by ${caseAuthorRoleLabel(entry.byRole)}` : ""}
                      {entry.at ? `, ${when(entry.at)}` : ""}
                      {entry.note ? `. ${entry.note}` : ""}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="support-case-meta">No status changes recorded yet.</div>
              )}
            </div>
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        {settled ? (
          <Button variant="outline-secondary" onClick={reopen} disabled={anyBusy}>
            {busy === "reopen" ? <Spinner animation="border" size="sm" /> : "Reopen case"}
          </Button>
        ) : null}
        <Button variant="outline-secondary" onClick={load} disabled={loading || anyBusy}>
          Refresh
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SupportCaseDetailPanel;
